import { DeleteOutline, Inventory2Outlined } from '@mui/icons-material'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

import CustomChip from '@/components/dataDisplay/CustomChip'
import EllipsisTypography from '@/components/dataDisplay/EllipsisTypography'
import type { ResourceSetMember } from '@/features/prequalification/localControl/types'
import { settleInSx } from '@/features/prequalification/localControl/utils/motion'

type ResourceSetResourceBasketProps = {
  resources: ResourceSetMember[]
  onRemove: (resourceId: string) => void
  /**
   * Optional gate: when it returns a reason, that resource's remove control is
   * disabled and the reason is shown as its tooltip (e.g. a resource with active
   * runs in the editor). Returning null keeps the resource removable (the default).
   */
  removalBlockReason?: (resource: ResourceSetMember) => string | null
}

/**
 * The builder's main pane: the resources chosen so far, with a count and a
 * per-row remove. The right-hand `AddResourcesPanel` feeds it; page-level state
 * is the source of truth.
 */
function ResourceSetResourceBasket({
  resources,
  onRemove,
  removalBlockReason,
}: Readonly<ResourceSetResourceBasketProps>) {
  const { t } = useTranslation()

  return (
    <Stack sx={{ gap: 2, height: '100%' }}>
      <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6">{t('prequalifications.resource_set.new.basket_title')}</Typography>
        <CustomChip
          key={resources.length}
          color="primary"
          label={String(resources.length)}
          size="small"
          sx={settleInSx}
          variant="outlined"
        />
      </Stack>

      {resources.length === 0 ? (
        <Stack
          sx={{
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            py: 6,
            px: 3,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 56,
              height: 56,
              borderRadius: '50%',
              color: 'primary.main',
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <Inventory2Outlined />
          </Box>
          <Stack sx={{ gap: 0.5, maxWidth: 340 }}>
            <Typography sx={{ fontWeight: 600 }} variant="body1">
              {t('prequalifications.resource_set.new.empty_basket_title')}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {t('prequalifications.resource_set.new.empty_basket')}
            </Typography>
          </Stack>
        </Stack>
      ) : (
        <Box
          aria-label={t('prequalifications.resource_set.new.basket_title')}
          // Cap the height and scroll so a large set can't render an unbounded,
          // fully-materialized list in a non-scrolling container.
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            maxHeight: 480,
            overflowY: 'auto',
          }}
        >
          {resources.map((resource, index) => {
            const blockReason = removalBlockReason?.(resource) ?? null
            const removeLabel = t('prequalifications.resource_set.resources.remove_action', {
              resourceName: resource.name,
            })
            return (
              <Stack
                key={resource.resourceId}
                sx={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 1,
                  px: 2,
                  borderTop: index === 0 ? 'none' : '1px solid',
                  borderColor: 'divider',
                  transition: 'background-color 120ms ease',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <EllipsisTypography tooltipTitle={resource.name} variant="body2">
                    {resource.name}
                  </EllipsisTypography>
                  <Typography color="text.secondary" variant="caption">
                    {resource.resourceId}
                  </Typography>
                </Stack>
                <Tooltip title={blockReason ?? removeLabel}>
                  {/* A disabled button can't host a tooltip, so wrap it in a focusable span. */}
                  <span>
                    <IconButton
                      aria-label={removeLabel}
                      data-testid="basket-remove-button"
                      disabled={Boolean(blockReason)}
                      size="small"
                      onClick={() => onRemove(resource.resourceId)}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            )
          })}
        </Box>
      )}
    </Stack>
  )
}

export default ResourceSetResourceBasket
